import React from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { Toaster } from 'sonner';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { LanguageProvider } from './contexts/LanguageContext';
import { Navbar } from './components/layout/Navbar';
import { Footer } from './components/layout/Footer';

// Pages
import Home from './pages/Home';
import ProviderList from './pages/ProviderList';
import ProviderDetail from './pages/ProviderDetail';
import Login from './pages/Auth/Login';
import Register from './pages/Auth/Register';
import AuthCallback from './pages/Auth/AuthCallback';
import ClientDashboard from './pages/Dashboard/ClientDashboard';
import ProviderDashboard from './pages/Dashboard/ProviderDashboard';
import Bookings from './pages/Bookings';
import BookingDetail from './pages/BookingDetail';
import PaymentSuccess from './pages/PaymentSuccess';
import Messages from './pages/Messages';
import ProviderProfile from './pages/ProviderProfile';
import AdminDashboard from './pages/Admin/AdminDashboard';
import AdminUsers from './pages/Admin/AdminUsers';
import AdminAds from './pages/Admin/AdminAds';
import AdminVerifications from './pages/Admin/AdminVerifications';
import AdminProviders from './pages/Admin/AdminProviders';
import AdminBookings from './pages/Admin/AdminBookings';
import AdminCommission from './pages/Admin/AdminCommission';
import AdminPayouts from './pages/Admin/AdminPayouts';
import AdminVouchers from './pages/Admin/AdminVouchers';
import AdminLogin from './pages/Admin/AdminLogin';
import AdminStripeDashboard from './pages/Admin/AdminStripeDashboard';
import ProviderBlog from './pages/ProviderBlog';
import ProviderPortfolio from './pages/ProviderPortfolio';
import ProviderStripeSetup from './pages/ProviderStripeSetup';
import ForgotPassword from './pages/Auth/ForgotPassword';
import ResetPassword from './pages/Auth/ResetPassword';
import ChangePassword from './pages/Auth/ChangePassword';

import './App.css';

// Protected Route Component
const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="spinner border-accent" />
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  return children;
};

// Provider Route Component
const ProviderRoute = ({ children }) => {
  const { isAuthenticated, user, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="spinner border-accent" />
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  if (user?.role !== 'provider') {
    return <Navigate to="/dashboard" replace />;
  }

  return children;
};

// Admin Route Component
const AdminRoute = ({ children }) => {
  const { isAuthenticated, user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="spinner border-accent" />
      </div>
    );
  }

  // Redirect to dedicated admin login page
  if (!isAuthenticated) {
    return <Navigate to="/admin/login" replace />;
  }

  if (user?.role !== 'admin') {
    return <Navigate to="/admin/login" replace />;
  }

  return children;
};

// Dashboard Redirect - routes to correct dashboard based on role
const DashboardRedirect = () => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="spinner border-accent" />
      </div>
    );
  }

  if (user?.role === 'admin') {
    return <Navigate to="/admin/dashboard" replace />;
  }
  if (user?.role === 'provider') {
    return <Navigate to="/provider/dashboard" replace />;
  }
  return <ClientDashboard />;
};

// App Router with Auth Check
const AppRouter = () => {
  const location = useLocation();
  
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }

  return (
    <Routes>
      {/* Public Routes */}
      <Route path="/" element={<Home />} />
      <Route path="/providers" element={<ProviderList />} />
      <Route path="/providers/:providerId" element={<ProviderDetail />} />
      <Route path="/services" element={<ProviderList />} />
      <Route path="/login" element={<Login />} />
      <Route path="/register" element={<Register />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/auth/callback" element={<AuthCallback />} />
      
      {/* Admin Login - Separate, isolated login page */}
      <Route path="/admin/login" element={<AdminLogin />} />
      
      {/* Protected Routes */}
      <Route 
        path="/dashboard" 
        element={
          <ProtectedRoute>
            <DashboardRedirect />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/bookings" 
        element={
          <ProtectedRoute>
            <Bookings />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/bookings/:bookingId" 
        element={
          <ProtectedRoute>
            <BookingDetail />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/payment/success" 
        element={
          <ProtectedRoute>
            <PaymentSuccess />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/messages" 
        element={
          <ProtectedRoute>
            <Messages />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/change-password" 
        element={
          <ProtectedRoute>
            <ChangePassword />
          </ProtectedRoute>
        } 
      />

      {/* Provider Routes */}
      <Route 
        path="/provider/dashboard" 
        element={
          <ProviderRoute>
            <ProviderDashboard />
          </ProviderRoute>
        } 
      />
      <Route 
        path="/provider/profile" 
        element={
          <ProviderRoute>
            <ProviderProfile />
          </ProviderRoute>
        } 
      />
      <Route 
        path="/provider/blog" 
        element={
          <ProviderRoute>
            <ProviderBlog />
          </ProviderRoute>
        } 
      />
      <Route 
        path="/provider/stripe-setup" 
        element={
          <ProviderRoute>
            <ProviderStripeSetup />
          </ProviderRoute>
        } 
      />

      {/* Public Provider Portfolio */}
      <Route path="/providers/:providerId/portfolio" element={<ProviderPortfolio />} />

      {/* Admin Routes */}
      <Route 
        path="/admin" 
        element={
          <AdminRoute>
            <AdminDashboard />
          </AdminRoute>
        } 
      />
      <Route 
        path="/admin/dashboard" 
        element={
          <AdminRoute>
            <AdminDashboard />
          </AdminRoute>
        } 
      />
      <Route 
        path="/admin/users" 
        element={
          <AdminRoute>
            <AdminUsers />
          </AdminRoute>
        } 
      />
      <Route 
        path="/admin/ads" 
        element={
          <AdminRoute>
            <AdminAds />
          </AdminRoute>
        } 
      />
      <Route 
        path="/admin/verifications" 
        element={
          <AdminRoute>
            <AdminVerifications />
          </AdminRoute>
        } 
      />
      <Route 
        path="/admin/providers" 
        element={
          <AdminRoute>
            <AdminProviders />
          </AdminRoute>
        } 
      />
      <Route 
        path="/admin/bookings" 
        element={
          <AdminRoute>
            <AdminBookings />
          </AdminRoute>
        } 
      />
      <Route 
        path="/admin/commission" 
        element={
          <AdminRoute>
            <AdminCommission />
          </AdminRoute>
        } 
      />
      <Route 
        path="/admin/payouts" 
        element={
          <AdminRoute>
            <AdminPayouts />
          </AdminRoute>
        } 
      />
      <Route 
        path="/admin/vouchers" 
        element={
          <AdminRoute>
            <AdminVouchers />
          </AdminRoute>
        } 
      />
      <Route 
        path="/admin/stripe" 
        element={
          <AdminRoute>
            <AdminStripeDashboard />
          </AdminRoute>
        } 
      />
      <Route 
        path="/admin/stripe-dashboard" 
        element={
          <AdminRoute>
            <AdminStripeDashboard />
          </AdminRoute>
        } 
      />

      {/* Catch all */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
};

// Layout wrapper
const Layout = ({ children }) => {
  const location = useLocation();
  const noFooterPaths = ['/messages', '/login', '/register', '/auth/callback'];
  const showFooter = !noFooterPaths.some(path => location.pathname.startsWith(path));

  return (
    <>
      <Navbar />
      <main className="min-h-screen">
        {children}
      </main>
      {showFooter && <Footer />}
    </>
  );
};

function App() {
  return (
    <BrowserRouter>
      <LanguageProvider>
        <AuthProvider>
          <Layout>
            <AppRouter />
          </Layout>
          <Toaster 
            position="top-right" 
            richColors 
            closeButton
            toastOptions={{
              className: 'font-body',
            }}
          />
        </AuthProvider>
      </LanguageProvider>
    </BrowserRouter>
  );
}

export default App;
